import { Empleado } from "src/app/shared/model/empleado/empleado.model";
import { Etapa } from "src/app/shared/model/gestion-proyectos/etapa/etapa";
import { Version } from "src/app/shared/model/gestion-proyectos/etapa/version.model";

/**
 * El equipo y las versiones de la etapa de una incidencia salen del proyecto de
 * gestión de proyectos (Mongo) que el tablero ya consulta y le pasa al detalle.
 *
 * - El equipo se pedía a getEmpleadosXPuestoEmpleado.do, que nunca lo pudo
 *   responder: la etapa es un id de Mongo (texto) y el servicio la espera en
 *   param5 como número, así que la petición no se leía y salía un 200 sin
 *   cuerpo (null en el front). Aun con un número, el DAO la busca en param4
 *   dentro de cos_proyecto_empXetapa, tabla que ya nadie llena.
 * - Los ids de las versiones también son de Mongo: consultaVersion.do espera
 *   un número y no los resuelve, así que el nombre se toma de la etapa.
 */

/** Lo que se lee del proyecto: sus etapas. */
export interface ProyectoConEtapas {
  etapas?: Etapa[] | null;
}

/** Ids iguales aunque uno llegue como número y el otro como texto; vacío nunca es igual. */
export function mismoId(a: unknown, b: unknown): boolean {
  return !esVacio(a) && !esVacio(b) && String(a) === String(b);
}

function esVacio(valor: unknown): boolean {
  return comoTexto(valor).trim() === "";
}

/** El valor como texto. Un id o un puesto llega como texto o como número; lo que no lo es (un objeto, nada) da "". */
function comoTexto(valor: unknown): string {
  if (typeof valor === "string") {
    return valor;
  }
  return typeof valor === "number" || typeof valor === "boolean" || typeof valor === "bigint" ? String(valor) : "";
}

export function buscaEtapa(
  proyecto: ProyectoConEtapas | null | undefined,
  idEtapa: unknown
): Etapa | null {
  const etapas = Array.isArray(proyecto?.etapas) ? proyecto.etapas : [];
  return etapas.find((etapa) => mismoId(etapa?.id, idEtapa)) ?? null;
}

/** Nombre del integrante armado con sus partes; si no las trae, el nombreCompleto guardado. */
export function nombreCompletoDe(empleado: Empleado | null | undefined): string {
  const persona = empleado?.persona;
  const armado = [persona?.nombre, persona?.apellidoPaterno, persona?.apellidoMaterno]
    .map((parte) => String(parte ?? "").trim())
    .filter(Boolean)
    .join(" ");
  return armado || String(persona?.nombreCompleto ?? "").trim();
}

/**
 * Todo el equipo de la etapa, de cualquier puesto: sin repetidos por usuario y
 * con persona.nombreCompleto lleno, que es lo que pintan los selectores.
 */
export function equipoDeEtapa(
  proyecto: ProyectoConEtapas | null | undefined,
  idEtapa: unknown
): Empleado[] {
  const recursos = buscaEtapa(proyecto, idEtapa)?.recursosAsignados;
  const vistos = new Set<string>();
  const equipo: Empleado[] = [];

  for (const recurso of Array.isArray(recursos) ? recursos : []) {
    const idUsuario = recurso?.usuario?.id;
    if (esVacio(idUsuario) || vistos.has(String(idUsuario))) {
      continue;
    }
    vistos.add(String(idUsuario));
    equipo.push({
      ...recurso,
      persona: { ...recurso.persona, nombreCompleto: nombreCompletoDe(recurso) },
    });
  }

  return equipo;
}

/** Los de la etapa con ese puesto ("Desarrollador", "Tester"), sin importar mayúsculas. */
export function equipoConPuesto(
  proyecto: ProyectoConEtapas | null | undefined,
  idEtapa: unknown,
  puesto: string
): Empleado[] {
  const buscado = normalizaPuesto(puesto);
  return equipoDeEtapa(proyecto, idEtapa).filter(
    (integrante) => normalizaPuesto(integrante.puesto?.puesto) === buscado
  );
}

function normalizaPuesto(valor: unknown): string {
  return comoTexto(valor).trim().toLowerCase();
}

/** Nombre del que tiene ese id de usuario en la lista, o null si no está. */
export function nombreDeIntegrante(
  lista: Empleado[] | null | undefined,
  idUsuario: unknown
): string | null {
  const integrante = (Array.isArray(lista) ? lista : []).find((empleado) =>
    mismoId(empleado?.usuario?.id, idUsuario)
  );
  return integrante ? nombreCompletoDe(integrante) || null : null;
}

export function versionesDeEtapa(
  proyecto: ProyectoConEtapas | null | undefined,
  idEtapa: unknown
): Version[] {
  const versiones = buscaEtapa(proyecto, idEtapa)?.versiones;
  return Array.isArray(versiones) ? versiones : [];
}

/** Color del chip de prioridad, el mismo del tablero; undefined si la prioridad no es una de las tres. */
export function colorDePrioridad(prioridad: unknown): string | undefined {
  switch (prioridad) {
    case "MEDIA":
      return "rgb(241, 180, 48)";
    case "ALTA":
      return "rgb(224, 0, 0)";
    case "BAJA":
      return "rgb(65,105,225)";
    default:
      return undefined;
  }
}

/** La versión con ese id: primero en la etapa de la incidencia y, si no está ahí, en las demás del proyecto. */
export function buscaVersion(
  proyecto: ProyectoConEtapas | null | undefined,
  idEtapa: unknown,
  idVersion: unknown
): Version | null {
  if (esVacio(idVersion)) {
    return null;
  }

  const enLaEtapa = versionesDeEtapa(proyecto, idEtapa).find((version) =>
    mismoId(version?.id, idVersion)
  );
  if (enLaEtapa) {
    return enLaEtapa;
  }

  const etapas = Array.isArray(proyecto?.etapas) ? proyecto.etapas : [];
  for (const etapa of etapas) {
    const versiones = Array.isArray(etapa?.versiones) ? etapa.versiones : [];
    const encontrada = versiones.find((version) => mismoId(version?.id, idVersion));
    if (encontrada) {
      return encontrada;
    }
  }
  return null;
}
